import React from "react";

import moment from "moment";

import { ArrayUtils, useGetFieldPractices, useInView } from "utils";

import { CROP_TYPE_FCPO } from "models";

import { Col, Icon, Row, SkeletonWrapper } from "components";

export const CropTimeline = ({ fieldId }: { fieldId: string }) => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  const { data: fieldPractices, isLoading } = useGetFieldPractices({ fieldId, inView, isAdmin: true });

  // Generate 7 years starting from current year - 5, including 2026
  const currentYear = moment().year();
  const years = Array.from({ length: 7 }, (_, index) => currentYear - 5 + index);

  // Group crops by year
  const cropsByYear = ArrayUtils.groupBy(fieldPractices?.crops ?? [], (crop) => crop?.year ?? "unknown");

  // Define crop type order: MAIN -> COVER -> UNDERSOWING
  const cropTypeOrder = [CROP_TYPE_FCPO.MAIN, CROP_TYPE_FCPO.COVER, CROP_TYPE_FCPO.UNDERSOWING];

  // Function to sort crops by type
  const sortCropsByType = (crops: NonNullable<typeof fieldPractices>["crops"]) =>
    crops.sort((a, b) => {
      const aIndex = cropTypeOrder.indexOf(a.cropType);
      const bIndex = cropTypeOrder.indexOf(b.cropType);
      return aIndex - bIndex;
    });

  return (
    <SkeletonWrapper isLoading={isLoading} type="block" height="9rem">
      <div ref={ref} className="practice-timeline">
        <Row>
          {/* prelint-ignore-col */}
          {years.map((year) => {
            const crops = cropsByYear?.[year] || [];
            const sortedCrops = sortCropsByType(crops);
            return (
              <Col key={year} className="practice-timeline-year">
                <div className="practice-year-header">
                  <span className="paragraph-s">{year}</span>
                </div>
                <div className="practice-year-content">
                  {sortedCrops.length === 0 ? (
                    <div className="practice-item practice-empty">No crops</div>
                  ) : (
                    sortedCrops.map((crop) => (
                      <div
                        key={crop.id}
                        className={`practice-item ${crop.verificationStatus || "unknown"}`}
                        title={`${crop.cropName} - ${crop.verificationStatus || "Unknown status"}`}
                      >
                        {crop.cropName}
                        {crop.hasResidue && <Icon iconName="tabler/leaf" className="icon inline margin-left-25 green-text" size="s" />}
                      </div>
                    ))
                  )}
                </div>
              </Col>
            );
          })}
        </Row>
      </div>
    </SkeletonWrapper>
  );
};
